/**
 * Emit hosts for the `feedback` analytics group — 3 functions
 * lifted out of `analyticsScribeModel`'s effects so a call site can emit
 * without holding a Rematch `dispatch`. Every payload is the effect's own.
 */
import { track } from '@/lib/analytics/track';
import { trackLegacyEvent } from '@/lib/analytics/track-legacy-event';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import { SegmentActionName, SegmentObjectName } from '@/models/analytics/segment-names';
import {
  type FeedbackEventTrackBaseProps,
  type FeedbackRequest,
  type FeedbackTags,
} from '@/types';
import { type CSATRating, FeedbackTargetType } from '@heidi/schema/ml-scribe';

export function trackFeedbackModalReasonSelected(reasons: FeedbackTags) {
  trackLegacyEvent({
    name: SegmentObjectName.Feedback,
    props: {
      action: SegmentActionName.FeedbackModalReasonSelected,
      feedbackTags: reasons,
    },
  });

  // V2 — feedback_updated
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.FeedbackUpdated,
    props: {
      update_action: 'reason_selected',
      value: Array.isArray(reasons)
        ? reasons.map((r) => r.reason).join(',')
        : String(reasons),
    },
  });
}

// Feedback
export function trackFeedbackModalSelectedRate(
  input: { rating: CSATRating } & FeedbackEventTrackBaseProps
) {
  trackLegacyEvent({
    name: SegmentObjectName.Feedback,
    props: {
      action: SegmentActionName.FeedbackModalSelectedRate,
      ...input,
    },
  });

  // V2 — feedback_updated
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.FeedbackUpdated,
    props: {
      update_action: 'rate_selected',
      value: String(input.rating),
    },
  });
}

export function trackNoteFeedbackSubmitted(
  input: FeedbackRequest & FeedbackEventTrackBaseProps
) {
  trackLegacyEvent({
    name: SegmentObjectName.Feedback,
    props: {
      action: SegmentActionName.FeedbackSubmitted,
      feedbackCategory: input.feedbackCategory,
      rating: input.rating,
      feedbackDetail: input.detail,
      feedbackTags: input.feedback,
      timestamp: input.timestamp,
      userId: input.userId,
      noteId: input.noteId,
      noteCategory: input.noteCategory,
      documentName: input.documentName,
    },
  });

  // V2 — feedback_submitted
  const feedbackTargetTypeMap: Record<string, FeedbackTargetType> = {
    note: FeedbackTargetType.CONSULT_NOTE,
    document: FeedbackTargetType.DOCUMENT,
    transcript: FeedbackTargetType.TRANSCRIPT,
  };

  track({
    options: { amplitude: false },
    event_name: TrackingEventName.FeedbackSubmitted,
    props: {
      type: 'rating',
      value: input.rating,
      feedback: input.detail || undefined,
      target_type:
        feedbackTargetTypeMap[input.feedbackCategory ?? ''] ??
        FeedbackTargetType.CONSULT_NOTE,
      target_id: input.noteId,
      feedback_tags: input.feedback ? JSON.stringify(input.feedback) : undefined,
      timestamp: input.timestamp,
      note_category: input.noteCategory,
      document_name: input.documentName,
    },
  });
}
